import { useRef, useEffect } from "react";
import { leafletLayer } from "protomaps-leaflet";
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

interface CrimeMapProps {
    selectedDate: string;
    crimeType: string;
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function formatReportedCrimePopup(properties: unknown): string {
    if (!properties || typeof properties !== "object") return "Reported crime";
    const p = properties as Record<string, unknown>;
    const lines: string[] = [];
    const category = p["Offense Category"];
    const offense = p["Offense"];
    const reportNum = p["Report Number"];
    const lat = p["Latitude"];
    const lng = p["Longitude"];

    if (category != null) lines.push(`<strong>Category</strong>: ${escapeHtml(String(category))}`);
    if (offense != null) lines.push(`<strong>Offense</strong>: ${escapeHtml(String(offense))}`);
    if (reportNum != null) lines.push(`<strong>Report #</strong>: ${escapeHtml(String(reportNum))}`);
    if (typeof lat === "number" && typeof lng === "number") {
        lines.push(
            `<strong>Location</strong>: ${lat.toFixed(5)}, ${lng.toFixed(5)}`
        );
    }
    return lines.length > 0 ? lines.join("<br/>") : "Reported crime";
}

function getGridIdFromProperties(properties: unknown): string | null {
    if (!properties || typeof properties !== "object") return null;
    const p = properties as Record<string, unknown>;
    const value = p["Grid Id"];
    if (value != null && String(value).trim() !== "") {
        return String(value);
    }
    return null;
}

type RiskBucket = "low" | "medium" | "high";

type RankedFeature = {
    feature: GeoJSON.Feature;
    yProb: number;
    color?: string;
    bucket?: RiskBucket;
};

function hexToRgb(hex: string) {
    const h = hex.replace("#", "");
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
    const toHex = (v: number) => v.toString(16).padStart(2, "0");
    return `#${toHex(Math.round(r))}${toHex(Math.round(g))}${toHex(Math.round(b))}`;
}

function lerpColor(c1: string, c2: string, t: number) {
    const a = hexToRgb(c1);
    const b = hexToRgb(c2);
    return rgbToHex({
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    });
}

function assignBucketedShades(features: RankedFeature[], highCount: number, mediumCount: number) {
    const sorted = [...features].sort((a, b) => b.yProb - a.yProb);
    const high = sorted.slice(0, highCount);
    const medium = sorted.slice(highCount, highCount + mediumCount);
    const low = sorted.slice(highCount + mediumCount);

    const paint = (rows: RankedFeature[], light: string, dark: string, bucket: RiskBucket) => {
        if (!rows.length) return;
        const probs = rows.map((r) => r.yProb);
        const min = Math.min(...probs);
        const max = Math.max(...probs);
        rows.forEach((r) => {
            const t = (r.yProb - min) / (max - min + 1e-9);
            r.bucket = bucket;
            r.color = lerpColor(light, dark, t);
        });
    };

    paint(high, "#ffcdd2", "#c62828", "high");
    // Use a warmer yellow range and cooler green range so light shades are visually distinct.
    paint(medium, "#fff3b0", "#f59e0b", "medium");
    paint(low, "#d1fae5", "#15803d", "low");
    return sorted;
}

function getBucketCountsByCrimeType(crimeType: string): { high: number; medium: number } {
    const normalized = crimeType.trim().toLowerCase();
    if (normalized === "violent-crime") {
        return { high: 3, medium: 7 };
    }
    if (normalized === "property-crime") {
        return { high: 5, medium: 10 };
    }
    if (normalized === "other-crime") {
        return { high: 7, medium: 15 };
    }
    return { high: 3, medium: 7 };
}

function CrimeMap({ selectedDate, crimeType }: CrimeMapProps) {
    const mapEl = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const geoJsonLayerRef = useRef<L.GeoJSON | null>(null);
    const reportedCrimesLayerRef = useRef<L.GeoJSON | null>(null);
    const legendControlRef = useRef<L.Control | null>(null);

    useEffect(() => {
        if (!mapEl.current) return;
        const isMobile = window.innerWidth < 768;

        const map = L.map(mapEl.current, {
            center: [47.6062, -122.3321], // Seattle coordinates
            zoom: 12,
            zoomSnap: 0.1,
            maxZoom: 22,
            minZoom: isMobile ? 10.8 : 11.4,
            zoomAnimation: true,
            maxBounds: L.latLngBounds(
                [47.475516, -122.456939], // Southwest corner (expanded)
                [47.754165, -122.216231]  // Northeast corner (expanded)
            ),
            maxBoundsViscosity: 1.0, // Prevents panning outside bounds
        });

        mapRef.current = map;

        const tiles = leafletLayer({
            url: "https://seattle-crime-predictor.com/assets/maps/seattle.pmtiles",
            attribution: "© OpenStreetMap",
            flavor: "light", 
        });
        
        tiles.addTo(map);

        const legend = (L as unknown as { control: (options?: L.ControlOptions) => L.Control }).control({
            position: "bottomright",
        });
        legend.onAdd = () => {
            const div = L.DomUtil.create("div", "crime-map-legend");
            div.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
            div.style.padding = "8px 10px";
            div.style.borderRadius = "8px";
            div.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.2)";
            div.style.fontSize = "12px";
            div.style.lineHeight = "1.4";
            div.style.color = "#111827";

            const itemStyle = "display:flex; align-items:center; gap:8px; margin-top:4px;";
            const swatchStyle =
                "display:inline-block; width:12px; height:12px; border-radius:50%; border:1px solid rgba(0,0,0,0.2);";
            const diamondStyle =
                "display:inline-block; width:10px; height:10px; background:#ef4444; border:1px solid #991b1b; transform:rotate(45deg);";

            div.innerHTML = `
                <div style="font-weight:600;">Crime Risk Legend</div>
                <div style="${itemStyle}">
                    <span style="${swatchStyle} background:#22c55e;"></span>
                    <span>Low probability</span>
                </div>
                <div style="${itemStyle}">
                    <span style="${swatchStyle} background:#facc15;"></span>
                    <span>Medium probability</span>
                </div>
                <div style="${itemStyle}">
                    <span style="${swatchStyle} background:#ef4444;"></span>
                    <span>High probability</span>
                </div>
                <div style="${itemStyle}">
                    <span style="${diamondStyle}"></span>
                    <span>Crime occurred</span>
                </div>
            `;
            return div;
        };
        legend.addTo(map);
        legendControlRef.current = legend;

        const updateLegendOffsets = () => {
            const container = legend.getContainer();
            if (!container) return;
            const isMobileViewport = window.innerWidth < 768;
            const bottomOffset = isMobileViewport ? 24 : 18;
            container.style.marginBottom = `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)`;
            container.style.marginRight = "8px";
        };
        updateLegendOffsets();
        
        const resizeMapAndLegend = () => {
            map.invalidateSize({ pan: false, animate: false });
            updateLegendOffsets();
        };

        const resizeTimeoutId = window.setTimeout(resizeMapAndLegend, 120);
        window.requestAnimationFrame(resizeMapAndLegend);
        window.addEventListener("resize", resizeMapAndLegend);

        return () => {
            window.clearTimeout(resizeTimeoutId);
            window.removeEventListener("resize", resizeMapAndLegend);
            if (geoJsonLayerRef.current) {
                map.removeLayer(geoJsonLayerRef.current);
                geoJsonLayerRef.current = null;
            }
            if (reportedCrimesLayerRef.current) {
                map.removeLayer(reportedCrimesLayerRef.current);
                reportedCrimesLayerRef.current = null;
            }
            if (legendControlRef.current) {
                map.removeControl(legendControlRef.current);
                legendControlRef.current = null;
            }
            map.remove();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const fetchGeoJsonData = async () => {
            if (geoJsonLayerRef.current) {
                map.removeLayer(geoJsonLayerRef.current);
                geoJsonLayerRef.current = null;
            }
            if (reportedCrimesLayerRef.current) {
                map.removeLayer(reportedCrimesLayerRef.current);
                reportedCrimesLayerRef.current = null;
            }

            const predictionUrl = `https://api.seattle-crime-predictor.com/api/v1/get-crime-data?date=${selectedDate}&crimeType=${crimeType}`;
            const reportedUrl = `https://api.seattle-crime-predictor.com/api/v1/get-reported-crime-data?date=${selectedDate}&crimeType=${crimeType}`;

            const [predictionOutcome, reportedOutcome] = await Promise.allSettled([
                fetch(predictionUrl).then(async (response) => {
                    if (!response.ok) {
                        throw new Error(`Prediction request failed: ${response.status}`);
                    }
                    return response.json();
                }),
                fetch(reportedUrl).then(async (response) => {
                    if (!response.ok) {
                        throw new Error(`Reported crimes request failed: ${response.status}`);
                    }
                    return response.json();
                }),
            ]);

            if (predictionOutcome.status === "fulfilled") {
                const geoJsonData = predictionOutcome.value;
                const ranked: RankedFeature[] = ((geoJsonData?.features ?? []) as GeoJSON.Feature[]).map((feature) => ({
                    feature,
                    yProb: Number(feature?.properties?.Y_Prob ?? 0),
                }));
                const { high: highBucketCount, medium: mediumBucketCount } = getBucketCountsByCrimeType(crimeType);
                assignBucketedShades(ranked, highBucketCount, mediumBucketCount);
                const colorByFeature = new Map<GeoJSON.Feature, string>();
                const riskLabelByFeature = new Map<GeoJSON.Feature, "Low" | "Medium" | "High">();
                const probabilityByFeature = new Map<GeoJSON.Feature, number>();
                ranked.forEach(({ feature, yProb, color, bucket }) => {
                    if (color) colorByFeature.set(feature, color);
                    probabilityByFeature.set(feature, yProb);
                    if (bucket === "high") riskLabelByFeature.set(feature, "High");
                    else if (bucket === "medium") riskLabelByFeature.set(feature, "Medium");
                    else riskLabelByFeature.set(feature, "Low");
                });

                // @ts-ignore - leaflet GeoJSON typing
                const geoJsonLayer = L.geoJSON(geoJsonData, {
                    style: (feature) => {
                        return {
                            fillColor: feature ? colorByFeature.get(feature) ?? "#e8f5e9" : "#e8f5e9",
                            fillOpacity: 0.5,
                            weight: 0,
                        };
                    },
                    onEachFeature: (feature, layer) => {
                        const gridId = getGridIdFromProperties(feature?.properties);
                        const probability = feature ? probabilityByFeature.get(feature) : undefined;
                        const popupLines = [
                            `<strong>Risk level</strong>: ${feature ? riskLabelByFeature.get(feature) ?? "Low" : "Low"}`,
                            `<strong>Probability</strong>: ${
                                probability != null && Number.isFinite(probability)
                                    ? `${(probability * 100).toFixed(2)}%`
                                    : "N/A"
                            }`,
                        ];
                        if (gridId) {
                            popupLines.push(`<strong>Grid ID</strong>: ${escapeHtml(gridId)}`);
                        }
                        layer.bindPopup(popupLines.join("<br/>"));
                    },
                });
                geoJsonLayer.addTo(map);
                geoJsonLayerRef.current = geoJsonLayer;
            } else {
                console.error("Error loading prediction GeoJSON:", predictionOutcome.reason);
            }

            if (reportedOutcome.status === "fulfilled") {
                const reportedGeoJson = reportedOutcome.value;
                const reportedCrimeIcon = L.divIcon({
                    className: "",
                    iconSize: [12, 12],
                    iconAnchor: [6, 6],
                    html: `<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                            <polygon points="6,1 11,6 6,11 1,6" fill="#ef4444" stroke="#991b1b" stroke-width="1" />
                           </svg>`,
                });
                // @ts-ignore - leaflet GeoJSON typing
                const reportedLayer = L.geoJSON(reportedGeoJson, {
                    pointToLayer: (_feature, latlng) =>
                        L.marker(latlng, {
                            icon: reportedCrimeIcon,
                        }),
                    onEachFeature: (feature, layer) => {
                        layer.bindPopup(formatReportedCrimePopup(feature.properties));
                    },
                });
                reportedLayer.addTo(map);
                reportedCrimesLayerRef.current = reportedLayer;
            } else {
                console.error("Error loading reported crimes GeoJSON:", reportedOutcome.reason);
            }
        };

        fetchGeoJsonData();
    }, [selectedDate, crimeType]);

    return (
        <div
            id="map"
            ref={mapEl}
            className="w-full h-full"
        />
    );
}

export default CrimeMap;